/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * File 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/file/File.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 8. 11.
 */
import * as parent from '@components/form/field/Field';
import * as uploader from '@utils/Uploader';
import { Button } from '@namespaces/components/Button';
import { Ajax } from '@utils/Ajax';
import { Dom } from '@utils/Dom';
import { Format } from '@utils/Format';
import { Html } from '@utils/Html';
import { Language, LanguageText } from '@utils/Language';
import { Exceptions } from '@utils/Exceptions';
import { Message } from '@components/message/Message';
import './File.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 업로드 URL
     */
    url?: string;

    /**
     * 업로드 허용파일
     */
    accept?: string;

    /**
     * 다중파일 선택여부
     */
    multiple?: boolean;

    /**
     * 자동업로드 여부 (false 인 경우, getValue() 함수 호출시 업로드를 한다.)
     */
    autoUpload?: boolean;

    /**
     * 파일선택 버튼 텍스트
     */
    text?: LanguageText;

    /**
     * 파일선택 버튼 아이콘
     */
    icon?: string;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class File extends parent.Field {
    /**
     * 기본 업로드 URL
     */
    static url: string = null;

    /**
     * 컴포넌트 확장타입
     */
    variant = 'file';

    /**
     * 업로드 URL
     */
    url: string;

    /**
     * 업로드 허용파일
     */
    accept: string | null = null;

    /**
     * 다중파일 선택여부
     */
    multiple: boolean | null = null;

    /**
     * 자동업로드 여부 (false 인 경우, getValue() 함수 호출시 업로드를 한다.)
     */
    autoUpload: boolean;

    /**
     * 파일선택 버튼 텍스트
     */
    text: LanguageText | null = null;

    /**
     * 파일선택 버튼 아이콘
     */
    icon: string | null = null;

    /**
     * 업로더클래스
     */
    uploader: uploader.Uploader = null;

    /**
     * 파일목록
     */
    attachments: Map<string, uploader.Attachment> = new Map();

    /**
     * 파일선택버튼
     */
    button: Button = null;

    /**
     * 버튼아이콘 DOM
     */
    $icon: Dom = null;

    /**
     * 버튼텍스트 DOM
     */
    $text: Dom = null;

    /**
     * 파일목록 DOM
     */
    $files: Dom = null;

    /**
     * File Input Dom
     */
    $input: Dom = null;

    /**
     * 에디터 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<File> = null) {
        super(properties);

        this.url = this.properties?.url ?? File.url ?? '/';
        this.accept = this.properties?.accept ?? '*';
        this.multiple = this.properties?.multiple === true;
        this.autoUpload = this.properties?.autoUpload !== false;
        this.text = this.properties?.text ?? Language.get('components.form.field.file.select');
        this.icon = this.properties?.icon ?? 'mi mi-upload';
        this.$droppable = this.$getFiles();
    }

    /**
     * 기본 업로드 URL 을 설정한다.
     *
     * @param url - 기본 업로드 URL
     */
    static setUrl(url: string): void {
        File.url = url;
    }

    /**
     * 업로더를 가져온다.
     *
     * @returns 업로더
     */
    getUploader(): uploader.Uploader {
        if (this.uploader === null) {
            this.uploader = new uploader.Uploader({
                url: this.url,
                update: async (draft_id, attachment, status) => this.#update(draft_id, attachment, status),
            });
        }

        return this.uploader;
    }

    /**
     * 파일선택버튼을 가져온다.
     *
     * @returns 파일선택버튼
     */
    getButton(): Button {
        if (this.button == null) {
            this.button = new Button({
                icon: this.icon,
                text: this.text,
                handler: () => {
                    this.select();
                },
            });
        }

        return this.button;
    }

    /**
     * 파일 INPUT Dom을 가져온다.
     *
     * @returns 파일 INPUT
     */
    $getInput(): Dom {
        if (this.$input == null) {
            this.$input = Html.create('input', { type: 'file', accept: this.accept });

            if (this.multiple == true) {
                this.$input.setAttr('multiple', 'multiple');
            }

            this.$input.on('change', (me, e: Event & { target: HTMLInputElement }) => {
                const attachments = this.getUploader().add(e.target.files);
                for (const attachment of attachments) {
                    this.attachments.set(attachment.attachment_id, attachment);
                    this.#print(attachment);
                }

                me.reset();

                if (this.autoUpload == true) {
                    this.getUploader().start();
                }
            });
        }

        return this.$input;
    }

    /**
     * 파일목록 DOM 을 가져온다.
     *
     * @return 파일목록 DOM
     */
    $getFiles(): Dom {
        if (this.$files === null) {
            this.$files = Html.create('div', { 'data-role': 'files' });

            Language.get('components.form.field.file.drag_help')
                .getText()
                .then((text) => {
                    this.$files.setAttr('data-drag-text', text);
                });

            this.$files.on('drop', async (_, e: DragEvent) => {
                if (e.dataTransfer?.types.includes('Files') == true) {
                    e.preventDefault();

                    const dropped = Array.from(e.dataTransfer.files);
                    const files = this.multiple === false ? dropped.slice(0, 1) : dropped;

                    if (this.multiple === false && this.attachments.size > 0) {
                        const isUploading = await this.getUploader().pause();

                        Message.confirm({
                            message: Language.get('components.form.field.file.select_one_help'),
                            handler: async (button) => {
                                if (button.value == 'ok') {
                                    for (const attachment of this.attachments.values()) {
                                        if (attachment.status == 'LOCAL') {
                                            await this.getUploader().delete(attachment.attachment_id);
                                        }
                                        this.#delete(attachment.attachment_id);
                                    }

                                    for (const attachment of this.getUploader().add(files)) {
                                        this.attachments.set(attachment.attachment_id, attachment);
                                        this.#print(attachment);
                                    }

                                    if (this.autoUpload == true) {
                                        this.getUploader().start();
                                    }
                                } else if (isUploading == true) {
                                    this.getUploader().start();
                                }
                            },
                        });
                    } else {
                        for (const attachment of this.getUploader().add(files)) {
                            this.attachments.set(attachment.attachment_id, attachment);
                            this.#print(attachment);
                        }

                        if (this.autoUpload == true) {
                            this.getUploader().start();
                        }
                    }
                }
            });
        }

        return this.$files;
    }

    /**
     * 현재 첨부파일 목록에 특정 id 를 가진 파일객체를 가져온다.
     *
     * @param id - 첨부파일고유값
     * @returns 첨부파일객체
     */
    getFile(id: string): uploader.Attachment | null {
        return this.attachments.get(id) ?? null;
    }

    /**
     * 파일을 선택한다.
     */
    async select(): Promise<void> {
        if (this.multiple === false && this.attachments.size > 0) {
            const isUploading = await this.getUploader().pause();

            Message.confirm({
                message: Language.get('components.form.field.file.select_one_help'),
                handler: async (button) => {
                    if (button.value == 'ok') {
                        for (const attachment of this.attachments.values()) {
                            if (attachment.status == 'LOCAL') {
                                await this.getUploader().delete(attachment.attachment_id);
                            }
                            this.#delete(attachment.attachment_id);
                        }

                        this.$getInput().getEl().click();
                    } else if (isUploading == true) {
                        this.getUploader().start();
                    }
                },
            });
        } else {
            this.$getInput().getEl().click();
        }
    }

    /**
     * 파일목록에서 파일을 제거한다.
     *
     * @param id - 첨부파일고유값
     */
    async delete(id: string): Promise<void> {
        /**
         * 진행중인 업로드가 있다면 일시중지한다.
         */
        const isUploading = await this.getUploader().pause();

        const file = this.getFile(id);
        if (file === null) {
            if (isUploading == true) {
                this.getUploader().start();
            }
            return;
        }

        Message.confirm({
            message: Language.get(file.status == 'LOCAL' ? 'components.form.field.file.cancel_help' : 'components.form.field.file.delete_help'),
            handler: async (button) => {
                if (button.value == 'ok') {
                    if (file.status == 'LOCAL') {
                        await this.getUploader().delete(id);
                    }
                    this.#delete(id);
                }

                if (isUploading == true) {
                    this.getUploader().start();
                }
            },
        });
    }

    /**
     * 업로드가 완료된 파일의 고유값을 가져온다.
     *
     * @return 첨부파일고유값
     */
    async getValue(): Promise<string | string[]> {
        /**
         * 새로 업로드된 파일이 확인하고, 새로 추가된 파일이 알지못하는 파일일 경우 예외를 출력한다.
         */
        const attachments = await this.getUploader().get();
        for (const attachment of attachments) {
            if (this.attachments.has(attachment.attachment_id) == false) {
                throw Exceptions.code('UNKNOWN_FILE');
                // @todo 자동으로 추가해주는 것이 나을 것인가?
            }
        }

        const value = [];
        for (const attachment of this.attachments.values()) {
            value.push(attachment.attachment_id);
        }

        if (this.multiple == true) {
            this.value = value;
        } else {
            this.value = value.at(-1) ?? null;
        }

        return this.value;
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     */
    async setValue(value: string | string[], is_init: boolean = false): Promise<void> {
        await this.lockValue();

        this.attachments.clear();

        if (value === null) {
            this.attachments.clear();
        } else {
            value = Array.isArray(value) == true ? value : [value];
            const results = await Ajax.get(this.url, { attachment_ids: JSON.stringify(value) });

            value = [];
            if (results.success == true) {
                for (const attachment of results.attachments) {
                    this.attachments.set(attachment.id, attachment);
                    this.#print(attachment);

                    if (this.multiple == false) {
                        break;
                    }
                }
            }
        }

        await this.releaseValue();
    }

    /**
     * 필드값이 비어있는지 확인한다.
     *
     * @returns 빈값여부
     */
    async isBlank(value: any = undefined): Promise<boolean> {
        return this.attachments.size == 0;
    }

    /**
     * 필드값이 유효한지 확인한다.
     *
     * @returns 유효성여부
     */
    async isValid(value: any = undefined): Promise<boolean> {
        if (this.isDisabled() === true) {
            return true;
        }

        /**
         * 유효성검증에서 필드값을 구할 경우 업로드를 시도하므로, 업로드 과정없이 유효성만 체크한다.
         */
        const validation = await this.validate();
        this.updateValidation(validation);

        return this.validation.valid;
    }

    /**
     * 필드값이 유효한지 확인한다.
     *
     * @param value - 유효성결과를 검증할 값 (정의되지 않은 경우, 현재 값)
     * @returns 필드값 유효성결과
     */
    protected async validate(value: any = undefined): Promise<{ valid: boolean; message?: string }> {
        if (this.isDisabled() === true) {
            return { valid: true };
        }

        let validation: { valid: boolean; message?: string } = { valid: true, message: null };
        const isBlank = await this.isBlank();
        if (this.allowBlank === false && isBlank == true) {
            validation.valid = false;
            validation.message = await Language.getError('REQUIRED').getText();
        }

        return validation;
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        this.$getContent().append(this.getButton().$getComponent());
        await this.getButton().render(true);
        this.$getContent().append(this.$getFiles());
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async remove(removeDom: boolean = true): Promise<void> {
        this.uploader?.destroy();
        this.attachments.clear();

        await super.remove(removeDom);
    }

    /**
     * 업로드 상태에 따라 파일을 갱신한다.
     *
     * @param draft_id - 임시파일고유값
     * @param attachment - 첨부파일객체
     * @param status - 업로드상태
     */
    #update(draft_id: string, attachment: uploader.Attachment, status: uploader.Status): void {
        const $file = Html.get('> div[data-role=file][data-id="' + draft_id + '"]', this.$getFiles());
        if ($file === null) {
            return;
        }

        /**
         * 업로드중인 파일이라면 업로드 상태에 따라 진행상황을 업데이트한다.
         */
        if (attachment.status == 'LOCAL') {
            $file.setAttr('data-upload-status', status.status);

            if (status.status == 'READING') {
                const $read = Html.get('progress[data-role=read]', $file);
                const progress = Math.round((status.readed / attachment.size) * 100).toString();
                $read.setStyleProperty('--progress', progress);
                $read.setAttr('value', progress);
            }

            if (status.status == 'UPLOADING') {
                const $upload = Html.get('progress[data-role=upload]', $file);
                const progress = Math.round((status.uploaded / attachment.size) * 100).toString();
                $upload.setStyleProperty('--progress', progress);
                $upload.setAttr('value', progress);
            }

            /**
             * 업로드가 실패하면 선택 시점에 낙관적으로 기록해둔 항목을 제거한다.
             * 그대로 두면 재제출시 실제로 업로드되지 않은 임시고유값이 값에 포함된다.
             * getValue() 호출시 Uploader.get() 이 던지는 예외로 Form 에 실패를 알린다.
             */
            if (status.status == 'FAILED') {
                this.#delete(draft_id);
            }
        }

        /**
         * 업로드가 완료된 경우
         */
        if (attachment.status != 'LOCAL') {
            this.attachments.delete(draft_id);
            this.attachments.set(attachment.attachment_id, attachment);
            this.#print(attachment, draft_id);
        }
    }

    /**
     * 파일을 출력한다.
     *
     * @param file - 출력할 파일객체
     * @param replace_id - 기존 파일을 대체할 경우 대체되는 파일고유값
     */
    #print(attachment: uploader.Attachment, replace_id: string = null): void {
        const $files = this.$getFiles();
        if ($files.getEl() === null) {
            return;
        }

        const $file = Html.create('div', {
            'data-role': 'file',
            'data-id': attachment.attachment_id,
            'data-status': attachment.status,
        });

        const $preview = Html.create('div', { 'data-role': 'preview' });
        const $icon = Html.create('i', {
            'data-role': 'file',
            'data-type': attachment.type,
            'data-mime': attachment.mime,
            'data-extension': attachment.extension,
        });
        $preview.append($icon);

        const $thumbnail = Html.create('div', { 'data-role': 'thumbnail' });
        if (attachment.thumbnail !== null) {
            $thumbnail.setStyle('background-image', 'url(' + attachment.thumbnail + ')');
        }
        /*
        if (attachment.thumbnail.indexOf('blob') === 0) {
            $image.setData('blob', attachment.thumbnail, false);
        }
        */
        $preview.append($thumbnail);

        /**
         * 파일읽기 프로그래스바
         */
        $preview.append(
            Html.create('progress', {
                'data-role': 'read',
                min: '0',
                max: '100',
                value: '0',
            })
        );

        /**
         * 파일업로드 프로그래스바
         */
        $preview.append(
            Html.create('progress', {
                'data-role': 'upload',
                min: '0',
                max: '100',
                value: '0',
            })
        );

        $file.append($preview);

        if (attachment.status !== 'LOCAL') {
            const $download = Html.create('a', {
                href: attachment.download,
                'data-action': 'download',
                download: attachment.name,
            });
            $download.html('<i></i>');
            $preview.append($download);
        }

        const $delete = Html.create('button', { type: 'button', 'data-action': 'delete' });
        $delete.html('<i></i>');
        $delete.on('click', () => {
            this.delete(attachment.attachment_id);
        });
        $preview.append($delete);

        const $label = Html.create('div', { 'data-role': 'label' });
        const $name = Html.create('b');
        $name.html(attachment.name);
        $label.append($name);
        $file.append($label);

        const $size = Html.create('small', { 'data-role': 'size' });
        $size.html(Format.size(attachment.size));
        $file.append($size);

        const $origin = Html.get('div[data-id="' + (replace_id ?? attachment.attachment_id) + '"]', $files);
        if ($origin === null) {
            $files.append($file);
        } else {
            $origin.replaceWith($file);
        }

        if (attachment.name.length >= 8) {
            let length = attachment.name.length - 6;
            while ($label.getOuterHeight() < $name.getOuterHeight()) {
                if (length <= 0) {
                    break;
                }

                $name.html(Format.substring(attachment.name, [length, 6]));
                --length;
            }
        }

        if (attachment.status == 'LOCAL') {
            const status = this.getUploader().getStatus(attachment.attachment_id);
            this.#update(attachment.attachment_id, attachment, status);
        }
    }

    /**
     * 파일을 제거한다.
     *
     * @param id - 제거할 파일고유값
     */
    #delete(id: string): void {
        Html.get('> div[data-id="' + id + '"]', this.$getFiles())?.remove();
        this.attachments.delete(id);
    }
}
